import { ToDo } from "../../Components/ToDo";
import { Step } from "../Step/Step";
import { Cash } from "../Step/Cash";
import { StepEvent, StepEventType } from "../Step/StepEvent";
import { StepType } from "../Step/StepType";
import { DateTimeSlider } from "../../Components/Utils/DateTimeSlider";
import { TimeUtils } from "../../Utils/TimeUtils";
import { TrainingStepState } from "./Models/TrainingStepState";
import { TrainingStepData } from "./Models/TrainingStepData";
import { Div, Line, Link, MultilineInput, Snack, Span, UnorderedList } from "@tblabs/truffle";
import { Order } from "../../Models/Order";
import { DateTimeProvider } from "../../Services/DateTimeProvider";
import { PrimaryButton } from "../../Components/Utils/PrimaryButton";
import { appendable } from "@tblabs/truffle/Core/appendable";


export class InstructionsList extends UnorderedList
{
    constructor(order: Order)
    {
        super(order.BasketItems.map(x => new Link(x.ProductInfo.Name, x.ProductInfo.Tutorial)));

        this.TextAlignLeft().MarginTop(4)
    }
}

export class TrainingStep extends Step<TrainingStepState>
{
    public Type = StepType.Training;
    public Icon = "🎓";
    public TitleForCustomer = () => `Szkolenie`;
    public Title = () => `Szkolenie`;

    public InitialState = TrainingStepState.NotPlannedYet;
    public WellDoneStates = [TrainingStepState.Done];
    public FailedDoneStates = [TrainingStepState.Cancelled, TrainingStepState.Undone]; // Undone is LEGACY

    public get AsEvents(): StepEvent[]
    {
        return [
            new StepEvent(StepEventType.Training, this.Order.Id, "🎓", "Szkolenie", this.Data.PlannedTime.value, this.IsDone)
        ];
    }
    public get Mana(): Cash[]
    {
        return [];
    }
    public get WhenPlanned(): Date
    {
        return this.Data.PlannedTime.value;
    }
    public get WhenDone(): Date
    {
        return this.Data.PlannedTime.value;
    }

    constructor(public Data: TrainingStepData)
    {
        super(Data);
    }

    public STATES = {
        [TrainingStepState.NotPlannedYet]: {
            StateName: "Nie zaplanowane",
            Customer: {
                Content: () => [`Termin szkolenia nie został jeszcze ustalony.\nJeśli zbliża się termin, możesz do nas zadzwonić i ustalić dogodny czas`],
            },
            Admin: {
                Content: () => [
                    `Czas szkolenia nie został jeszcze ustalony`,
                    new ToDo(
                        `Ustal termin szkolenia`,
                        new DateTimeSlider(this.Data.PlannedTime))
                ],
                Actions: {
                    "✅": () => this.GoTo(TrainingStepState.Planning),
                    "❌": () => this.GoTo(TrainingStepState.Canceling),
                },
            },
        },
        [TrainingStepState.Planning]: {
            StateName: "Planowanie w toku",
            Customer: {
                Content: () => [`🕓 Szkolenie jest właśnie planowane...`],
            },
            Admin: {
                Content: () => [
                    new ToDo(
                        `Ustal termin szkolenia`,
                        new DateTimeSlider(this.Data.PlannedTime))],
                Actions: {
                    "✅": () => this.GoTo(TrainingStepState.Planned),
                    "❌": () => this.GoTo(TrainingStepState.NotPlannedYet),
                },
            },
        },
        [TrainingStepState.Planned]: {
            StateName: "Zaplanowane",
            Timeout: () => this.Data.PlannedTime,
            Customer: {
                Content: () => [
                    `Szkolenie wstępnie zaplanowano na ${TimeUtils.FormattedDate(this.Data.PlannedTime)}. Odbędzie się przez telefon i potrwa około 10 minut`,
                    new Line(),
                    `Możesz wstępnie zapoznać się z materiałami szkoleniowymi:`,
                    new InstructionsList(this.Order),
                    new Line(),
                    `Jeśli masz już sprzęt: zadzwoń. Im wcześniej go uruchomimy tym lepiej!`
                ],
            },
            Admin: {
                Content: () => [
                    `Szkolenie zaplanowano na:\n${TimeUtils.FormattedDate(this.Data.PlannedTime)}`,
                    new PrimaryButton("Uruchom TX0000"),
                    new ToDo(`Jeśli szkolenie się odbyło - kliknij ✅\nJeśli zostało odwołane - kliknij ❌ i podaj powód odwołania`)
                ],
                Actions: {
                    "✅": () => { this.Data.DoneTime.value = DateTimeProvider.Now; this.GoTo(TrainingStepState.Done); },
                    "❌": () => this.GoTo(TrainingStepState.Canceling),
                },
            },
        },
        [TrainingStepState.Self]: {
            StateName: "Samoszkolenie",
            Timeout: () => this.Data.PlannedTime,
            Customer: {
                Content: () => [
                    `Zapoznaj się z materiałami szkoleniowymi:`,
                    new InstructionsList(this.Order),
                    ...this.AccessInfo(this.Order),
                ],
            },
            Admin: {
                Content: () => [
                    `Klient otrzymał materiały do samoszkolenia`,
                    new PrimaryButton("Uruchom TX0000"),
                    new ToDo(`Jeśli szkolenie się odbyło - kliknij ✅\nJeśli zostało odwołane - kliknij ❌ i podaj powód odwołania`)
                ],
                Actions: {
                    "✅": () => { this.Data.DoneTime.value = DateTimeProvider.Now; this.GoTo(TrainingStepState.Done); },
                    "❌": () => this.GoTo(TrainingStepState.Canceling),
                },
            },
        },
        [TrainingStepState.Done]: {
            StateName: "Wykonane",
            Customer: {
                Content: () => [
                    `✅ Szkolenie zakończone`,
                    new Line(),
                    `Materiały szkoleniowe odnajdziesz tutaj:`,
                    new InstructionsList(this.Order),
                    ...this.AccessInfo(this.Order),
                ],
            },
            Admin: {
                Content: () => [`✅ Szkolenie zakończone`],
            },
        },
        [TrainingStepState.Canceling]: {
            StateName: "Odwoływanie w toku",
            Customer: {
                Content: () => [
                    `✖️ Szkolenie jest właśnie odwoływane...`,
                ],
            },
            Admin: {
                Content: () => [
                    new ToDo(
                        `Podaj powód odwołania szkolenia (widoczny dla klienta!)`,
                        new MultilineInput(this.Data.CancelationReason).Placeholder("Powód odwołania szkolenia").Rows(5),
                        `\nlub wróć do planowania szkolenia`
                    )],
                Actions: {
                    "✅": () => this.GoTo(TrainingStepState.Cancelled),
                    "✖️": () => this.GoTo(TrainingStepState.NotPlannedYet),
                },
            },
        },
        [TrainingStepState.Undone]: { // [LEGACY]
            StateName: "Nie wykonane",
            Customer: {
                Content: () => [`❌ Szkolenie zostało odwołane`],
            },
            Admin: {
                Content: () => [`❌ Szkolenie zostało odwołane z powodu: ${this.Data.CancelationReason.value || "nie podano"}`],
            },
        },
        [TrainingStepState.Cancelled]: {
            StateName: "Odwołane",
            Customer: {
                Content: () => [`❌ Szkolenie zostało odwołane z powodu: ${this.Data.CancelationReason.value || "nie podano"}`],
            },
            Admin: {
                Content: () => [`❌ Szkolenie zostało odwołane z powodu: ${this.Data.CancelationReason.value || "nie podano"}`],
            },
        },
    };

    private AccessInfo(order: Order): appendable[]
    {
        return order.BasketItems
            .filter(x => x.AccessData.value)
            .flatMap(x => 
            {
                const data = JSON.parse(x.AccessData.value) as AccessData;

                return [
                    (data.Access === "TX-Watcher" && data.Version === "4.0.0") && new Div().Append(
                        "Aby otworzyć podgląd do kamery, kliknij:",
                        new PrimaryButton("Otwórz podgląd").Margin(8).OnClick(() => { window.open(data.LaunchLink, "_blank"); }),
                        new Div().TextAlignLeft().Append(
                        "lub wejdź na stronę:\n",
                        new Link(data.Link).OpenInNewCard(),
                        "\ni wpisz: ", new CopiableText(data.Password).Bold(),
                        ),
                    ).Background("#fff").Padding(12).BorderRadius(8).Border(1, "#eee", 8),
                ];
            });
    }
}

export class CopiableText extends Span
{
    constructor(text: string)
    {
        super(text + " 📋");

        this.CursorPointer()

        this.OnClick(() =>
        {
            CopyToClipboard(text);
            new Snack("📋 Skopiowano do schowka", text, { autoCloseTimeout: 2000 });
        });
    }
}


export interface AccessData
{
    Access: string;
    Version: string;
    DeviceId: string;
    Password: string;
    Link: string;
    LaunchLink: string
}

export function CopyToClipboard(textToCopy: string): Promise<string>
{
    try
    {
        // navigator clipboard api needs a secure context (https)
        if (navigator.clipboard && window.isSecureContext)
        {
            // console.log("1111111111111");
            return new Promise((res: (message: string) => void, rej: (message: string) => void) => 
            {
                // navigator clipboard api method'
                try
                {
                    navigator.clipboard.writeText(textToCopy);

                    res("Success");
                }
                catch (error: any)
                {
                    rej("Can not copy to clipboard: " + error.message);
                }
            })
        }
        else
        {
            // console.log("2222222222222");
            // text area method
            let textArea = document.createElement("textarea");
            textArea.value = textToCopy;
            // make the textarea out of viewport
            textArea.style.position = "fixed";
            textArea.style.left = "-999999px";
            textArea.style.top = "-999999px";
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            return new Promise((res: (message: string) => void, rej: (message: string) => void) =>
            {
                // here the magic happens
                document.execCommand('copy') ? res("Copy to clipboard succeeded") : rej("Copy to clipboard failed");
                textArea.remove();
            });
        }
    }
    catch (error: any)
    {
        console.error("Can not copy to clipboard: " + error.message);
        return Promise.reject("Can not copy to clipboard: " + error.message);
        // throw new Error("Can not copy to clipboard: " + error.message);
    }
}